import { Component, inject, OnDestroy, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from '@app/material.module';
import { RagApiService } from '@services/api/rag/rag-api.service';
import { CoreService } from '@services/core.service';
import { UserStorageService } from '@services/local-storage/user-storage/user-storage.service';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { User } from '@interfaces/user';
import { Company } from '@interfaces/company';

export interface ChatAttachment {
  name: string;
  url: SafeUrl;
  type: 'image' | 'audio' | 'document';
}

export interface ChatMessage {
  role: 'user' | 'bot';
  text: string;
  timestamp: Date;
  attachments?: ChatAttachment[];
}

const ACCEPTED_EXTENSIONS =
  '.jpg,.jpeg,.png,.gif,.webp,.bmp,' +
  '.mp3,.wav,.m4a,.ogg,.webm,.flac,' +
  '.pdf,.txt,.md,.csv,.json,.html,.htm';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    TablerIconsModule,
    MaterialModule,
    TranslateModule
  ],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
})
export class ChatbotComponent implements OnInit, OnDestroy {
  chatOpen = false;
  messages: ChatMessage[] = [];
  inputText = '';
  loading = false;
  isRecording = false;
  pendingAttachments: File[] = [];
  readonly acceptedExtensions = ACCEPTED_EXTENSIONS;
  companyId: number | null = null;
  sessionId = crypto.randomUUID();
  roomId: string | undefined;
  knowledgeScope = 'uploaded';
  userAvatar = environment.avatar_url;

  private ragService = inject(RagApiService);
  private translate = inject(TranslateService);
  private coreService = inject(CoreService);
  private userStorageService = inject(UserStorageService);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private userSub!: Subscription;
  private routerSub!: Subscription;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recognition: any = null;
  private transcribedText = '';
  private attachmentBlobUrls: string[] = [];
  private pendingAudioBlob: Blob | null = null;
  private recorderStopped = false;
  private recognitionStopped = false;

  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chatInput') chatInput!: ElementRef<HTMLTextAreaElement>;

  /** Altezza massima dell'input in px prima di attivare lo scroll interno (~5 righe). */
  private readonly maxInputHeight = 120;

  /** Spostamento (drag) del pannello rispetto alla sua posizione ancorata. */
  dragOffset = { x: 0, y: 0 };
  dragging = false;
  private dragStart = { x: 0, y: 0 };
  private dragOrigin = { x: 0, y: 0 };

  /** Dimensioni del pannello, ridimensionabili col mouse dai bordi alto/sinistro. */
  private readonly normalPanelSize = { width: 340, height: 460 };
  panelSize = { ...this.normalPanelSize };
  resizing = false;
  private resizeDir: 'left' | 'top' | 'corner' = 'corner';
  private resizeStart = { x: 0, y: 0 };
  private resizeOrigin = { width: 0, height: 0 };
  private readonly minPanelWidth = 300;
  private readonly minPanelHeight = 360;

  ngOnInit() {
    // Il chatbot è globale (montato nel layout): teniamo aggiornato il room_id
    // leggendolo dall'URL ad ogni navigazione, così su una stanza la chat è
    // contestualizzata, altrove resta un assistente generico.
    this.roomId = this.extractRoomId(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.roomId = this.extractRoomId((event as NavigationEnd).urlAfterRedirects);
      });

    this.userStorageService.getUserStorage().then(user => {
      const companyId = this.resolveCompanyId(user);
      if (companyId != null) this.companyId = companyId;
    });
    this.userSub = this.userStorageService.user$.subscribe(user => {
      const companyId = this.resolveCompanyId(user);
      if (companyId != null) this.companyId = companyId;
      this.userAvatar = user?.avatar || environment.avatar_url;
    });
  }

  /**
   * L'API /users/me può restituire `company` come oggetto ({ id, ... }) o,
   * a seconda del serializer, come semplice PK numerica: gestiamo entrambi.
   */
  private resolveCompanyId(user: User | null): number | null {
    const company = user?.company as Company | number | null | undefined;
    if (company == null) return null;
    return typeof company === 'number' ? company : (company.id ?? null);
  }

  /**
   * Estrae l'uuid della stanza da un URL tipo `/support/room/<uuid>`.
   * Ritorna undefined per la lista (`/support/room/list`) o altre pagine.
   */
  private extractRoomId(url: string): string | undefined {
    const match = url.match(/\/support\/room\/([^/?#]+)/);
    const segment = match?.[1];
    return segment && segment !== 'list' ? segment : undefined;
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    if (this.isRecording) {
      this.mediaRecorder?.stop();
      this.recognition?.stop();
    }
    this.attachmentBlobUrls.forEach(url => URL.revokeObjectURL(url));
  }

  sendMessage() {
    const text = this.inputText.trim();
    if ((!text && this.pendingAttachments.length === 0) || this.loading) return;

    const attachments = this.buildDisplayAttachments([...this.pendingAttachments]);

    this.messages.push({
      role: 'user',
      text,
      attachments: attachments.length > 0 ? attachments : undefined,
      timestamp: new Date()
    });

    const filesToSend = [...this.pendingAttachments];
    this.inputText = '';
    this.pendingAttachments = [];
    this.resetInputHeight();
    this.scrollToBottom();

    this.callRag(text || this.translate.instant('chatbot.attachment_message'), filesToSend);
  }

  /**
   * Avvia una nuova sessione di chat: azzera la conversazione, gli allegati in
   * sospeso e genera un nuovo session_id lato client.
   */
  startNewSession(): void {
    if (this.isRecording) this.stopRecording();
    this.messages = [];
    this.inputText = '';
    this.pendingAttachments = [];
    this.attachmentBlobUrls.forEach(url => URL.revokeObjectURL(url));
    this.attachmentBlobUrls = [];
    this.sessionId = crypto.randomUUID();
    this.loading = false;
    this.resetInputHeight();
  }

  /** Invia con Invio; Shift+Invio inserisce un ritorno a capo. */
  onEnterKey(event: Event): void {
    event.preventDefault();
    this.sendMessage();
  }

  /** Adatta l'altezza dell'input al contenuto, fino a maxInputHeight (poi scrolla). */
  autoGrowInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, this.maxInputHeight) + 'px';
  }

  /** Riporta l'input all'altezza di una singola riga dopo l'invio. */
  private resetInputHeight(): void {
    setTimeout(() => {
      const el = this.chatInput?.nativeElement;
      if (el) el.style.height = 'auto';
    });
  }

  /** Avvia il trascinamento del pannello dall'header (esclusi i pulsanti). */
  startDrag(event: PointerEvent): void {
    if ((event.target as HTMLElement).closest('button')) return;
    this.dragging = true;
    this.dragStart = { x: event.clientX, y: event.clientY };
    this.dragOrigin = { ...this.dragOffset };
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onDrag(event: PointerEvent): void {
    if (this.resizing) {
      this.onResize(event);
      return;
    }
    if (!this.dragging) return;
    this.dragOffset = {
      x: this.dragOrigin.x + (event.clientX - this.dragStart.x),
      y: this.dragOrigin.y + (event.clientY - this.dragStart.y),
    };
  }

  @HostListener('document:pointerup')
  endDrag(): void {
    this.dragging = false;
    this.resizing = false;
  }

  /**
   * Avvia il ridimensionamento dal bordo sinistro, superiore o dall'angolo
   * in alto a sinistra. Il pannello è ancorato in basso a destra, quindi
   * cresce verso l'alto/sinistra man mano che si trascina in quelle direzioni.
   */
  startResize(event: PointerEvent, dir: 'left' | 'top' | 'corner'): void {
    this.resizing = true;
    this.resizeDir = dir;
    this.resizeStart = { x: event.clientX, y: event.clientY };
    this.resizeOrigin = { ...this.panelSize };
    event.preventDefault();
    event.stopPropagation();
  }

  private onResize(event: PointerEvent): void {
    const dx = event.clientX - this.resizeStart.x;
    const dy = event.clientY - this.resizeStart.y;
    const max = this.maxPanelSize();

    if (this.resizeDir === 'left' || this.resizeDir === 'corner') {
      this.panelSize.width = this.clamp(
        this.resizeOrigin.width - dx, this.minPanelWidth, max.width);
    }
    if (this.resizeDir === 'top' || this.resizeDir === 'corner') {
      this.panelSize.height = this.clamp(
        this.resizeOrigin.height - dy, this.minPanelHeight, max.height);
    }
  }

  /**
   * Massima dimensione consentita al pannello, in base al viewport. Lascia dei
   * margini per non toccare i bordi e per lasciare spazio al FAB sottostante.
   * Usata sia dal resize manuale sia dal pulsante di massimizzazione.
   */
  private maxPanelSize(): { width: number; height: number } {
    return {
      width: Math.max(this.minPanelWidth, window.innerWidth - 48),
      height: Math.max(this.minPanelHeight, window.innerHeight - 120),
    };
  }

  /**
   * True quando il pannello è più grande della sua dimensione normale, sia che
   * l'ingrandimento arrivi dal pulsante sia da un resize manuale: in tal caso
   * l'header mostra l'icona per rimpicciolire alla dimensione normale.
   */
  get isExpanded(): boolean {
    return this.panelSize.width > this.normalPanelSize.width ||
      this.panelSize.height > this.normalPanelSize.height;
  }

  /**
   * Ingrandisce il pannello al massimo possibile oppure, se già ingrandito
   * (anche manualmente), lo riporta alla dimensione normale.
   */
  toggleMaximize(): void {
    if (this.isExpanded) {
      this.panelSize = { ...this.normalPanelSize };
    } else {
      this.panelSize = this.maxPanelSize();
      // Ancorato in basso a destra: azzeriamo lo spostamento così cresce
      // riempiendo il viewport dall'angolo di ancoraggio.
      this.dragOffset = { x: 0, y: 0 };
    }
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  onFilesSelected(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;
    this.pendingAttachments = [...this.pendingAttachments, ...Array.from(files)];
    (event.target as HTMLInputElement).value = '';
  }

  removeAttachment(index: number) {
    this.pendingAttachments = this.pendingAttachments.filter((_, i) => i !== index);
  }

  getAttachmentIcon(file: File): string {
    if (file.type.startsWith('image/')) return 'photo';
    if (file.type.startsWith('audio/')) return 'microphone';
    if (file.type === 'application/pdf') return 'file-type-pdf';
    return 'file';
  }

  startRecording() {
    if (!navigator.mediaDevices?.getUserMedia) return;

    this.transcribedText = '';
    this.pendingAudioBlob = null;
    this.recorderStopped = false;
    this.recognitionStopped = false;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = this.getRecognitionLang();
      this.recognition.onresult = (event: any) => {
        let fullTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          fullTranscript += event.results[i][0].transcript;
        }
        this.transcribedText = fullTranscript;
      };
      this.recognition.onerror = () => {
        this.recognitionStopped = true;
        this.trySendAudio();
      };
      this.recognition.onend = () => {
        this.recognitionStopped = true;
        this.trySendAudio();
      };
      this.recognition.start();
    } else {
      this.recognitionStopped = true;
    }

    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      this.audioChunks = [];
      this.mediaRecorder = new MediaRecorder(stream);

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        this.pendingAudioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.recorderStopped = true;
        this.trySendAudio();
      };

      this.mediaRecorder.start();
      this.isRecording = true;
    }).catch(() => {
      this.isRecording = false;
    });
  }

  stopRecording() {
    this.recognition?.stop();
    this.mediaRecorder?.stop();
    this.isRecording = false;
  }

  private trySendAudio() {
    if (!this.recorderStopped || !this.recognitionStopped) return;

    const blob = this.pendingAudioBlob!;
    const audioFile = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
    const text = this.transcribedText.trim();

    const attachments = this.buildDisplayAttachments([audioFile]);

    this.messages.push({ role: 'user', text, attachments, timestamp: new Date() });
    this.scrollToBottom();

    this.callRag(text || this.translate.instant('chatbot.audio_message'), [audioFile]);

    this.pendingAudioBlob = null;
    this.recorderStopped = false;
    this.recognitionStopped = false;
  }

  private callRag(text: string, attachments: File[] = []) {
    this.loading = true;
    const language = (localStorage.getItem('app_language') || this.coreService.getLanguage() || 'en').toUpperCase();
    const company_id = this.companyId ?? 0;

    this.ragService.chat({
      session_id: this.sessionId,
      message: text,
      language,
      company_id,
      room_id: this.roomId,
      knowledge_scope: this.knowledgeScope,
      attachments: attachments.length > 0 ? attachments : undefined,
    }).subscribe({
      next: (res) => {
        this.messages.push({ role: 'bot', text: res.data.answer, timestamp: new Date() });
        this.loading = false;
        this.scrollToBottom();
      },
      error: () => {
        this.messages.push({ role: 'bot', text: this.translate.instant('chatbot.error'), timestamp: new Date() });
        this.loading = false;
        this.scrollToBottom();
      }
    });
  }

  private buildDisplayAttachments(files: File[]): ChatAttachment[] {
    return files.map(file => {
      const rawUrl = URL.createObjectURL(file);
      this.attachmentBlobUrls.push(rawUrl);
      return {
        name: file.name,
        url: this.sanitizer.bypassSecurityTrustUrl(rawUrl),
        type: this.getFileType(file),
      };
    });
  }

  private getFileType(file: File): 'image' | 'audio' | 'document' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  }

  private getRecognitionLang(): string {
    const lang = (localStorage.getItem('app_language') || this.coreService.getLanguage() || 'en').toLowerCase();
    const map: Record<string, string> = { en: 'en-US', it: 'it-IT', de: 'de-DE', es: 'es-ES' };
    return map[lang] ?? 'en-US';
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop =
          this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 50);
  }
}
